// engine-exception: OAuth consent is an auth-flow page outside the shell (like invite acceptance) — one pending request, approve or decline, then a browser redirect to the app that asked; not a resource screen
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth';
import { useApi } from '../../engine/api/context';
import { ApiError } from '../../engine/api/client';
import { AuthCard, AuthScene } from '../../components/authui';
import { SignedInAs } from '../../pages/OnboardingGate';

// An app (an AI assistant over MCP, docs/mcp.md) asked to act as this user. GET /oauth/authorize sent
// the browser here with ?request=<id>; this page shows who is asking and for what, and the user's
// answer (POST /v1/oauth/requests/:id/decision) returns the URL to send the browser back to.
// Signed out: the request id is kept for after sign-in (OnboardingGate brings them back).

export const OAUTH_REQUEST_KEY = 'hf.oauth_request';

interface ConsentView { id: string; client_id: string; client_name: string | null; redirect_host: string; scopes: string[]; expires_at: string }

const SCOPE_TEXT: Record<string, string> = {
  'mcp:read': 'Read what you can read in NovaPSA — tickets, clients, projects and the rest — as you.',
  'mcp:write': 'Make changes as you (create, update and run actions), when your organisation allows automated changes.',
};

function remember(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(OAUTH_REQUEST_KEY, id);
    else sessionStorage.removeItem(OAUTH_REQUEST_KEY);
  } catch { /* storage blocked */ }
}

export function OAuthConsentPage() {
  const { session } = useAuth();
  const api = useApi();
  const [params] = useSearchParams();
  const requestId = params.get('request');
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const view = useQuery<ConsentView, ApiError>({
    queryKey: ['oauth-request', requestId],
    queryFn: () => api.request<ConsentView>(`/v1/oauth/requests/${encodeURIComponent(requestId!)}`),
    enabled: Boolean(session && requestId),
    retry: false,
  });

  if (!requestId) {
    return (
      <AuthScene>
        <AuthCard title="Nothing to authorize" subtitle="This link is missing its request.">
          <p className="muted" style={{ textAlign: 'center' }}>Start again from the app you were connecting.</p>
        </AuthCard>
      </AuthScene>
    );
  }
  if (!session) {
    remember(requestId);
    return (
      <AuthScene>
        <AuthCard title="Sign in to continue" subtitle="An app is asking to connect to your NovaPSA account. Sign in first; you will come back here to decide.">
          <Link className="btn primary block lift" to="/login">Sign in</Link>
        </AuthCard>
      </AuthScene>
    );
  }
  remember(null);

  const decide = async (approve: boolean) => {
    setBusy(approve ? 'approve' : 'deny');
    setError(null);
    try {
      const r = await api.request<{ redirect_to: string }>(`/v1/oauth/requests/${encodeURIComponent(requestId)}/decision`, { method: 'POST', body: { approve } });
      window.location.assign(r.redirect_to);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : String(e));
      setBusy(null);
    }
  };

  if (view.isLoading) {
    return (
      <AuthScene>
        <AuthCard><div className="progress-line" style={{ maxWidth: 200, margin: 'var(--sp-3) auto' }} /></AuthCard>
      </AuthScene>
    );
  }
  if (view.error || !view.data) {
    return (
      <AuthScene>
        <AuthCard title="This request can’t be used" footer={<SignedInAs />}>
          <p className="muted" style={{ textAlign: 'center' }}>{view.error?.detail ?? 'It may have expired — start again from the app.'}</p>
        </AuthCard>
      </AuthScene>
    );
  }
  const v = view.data;
  const name = v.client_name || 'An app';
  return (
    <AuthScene>
      <AuthCard title={`${name} wants to use your NovaPSA account`} subtitle={`It will return to ${v.redirect_host}.`} footer={<SignedInAs />}>
        <ul className="hf-consent" aria-label="What it will be able to do">
          {v.scopes.map((s) => <li key={s}>{SCOPE_TEXT[s] ?? s}</li>)}
        </ul>
        <p className="muted" style={{ fontSize: 'var(--t-13)' }}>It acts as you, with exactly your permissions in this organisation. You can disconnect it at any time from the app.</p>
        {error && <div className="auth-msg err" role="alert">⚠ {error}</div>}
        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
          <button type="button" className="btn block" disabled={busy !== null} onClick={() => void decide(false)}>Cancel</button>
          <button type="button" className="btn primary block lift" disabled={busy !== null} onClick={() => void decide(true)}>{busy === 'approve' ? 'Connecting…' : 'Allow'}</button>
        </div>
      </AuthCard>
    </AuthScene>
  );
}
